/**
 * Async wrapper over `window.localStorage` (`getItem`/`setItem`/`removeItem`/
 * `multiRemove`/`clear`). Every method returns a Promise so call sites can
 * `await`/`.then()` uniformly regardless of the backing store.
 *
 * Guarded against SSR/no-DOM environments so a call in a non-browser
 * context resolves to a null/no-op rather than throwing.
 */

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

export const localStorageAsync = {
  async getItem(key: string): Promise<string | null> {
    if (!hasStorage()) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  async setItem(key: string, value: string): Promise<void> {
    if (!hasStorage()) return;
    try {
      window.localStorage.setItem(key, value);
    } catch {}
  },

  async removeItem(key: string): Promise<void> {
    if (!hasStorage()) return;
    try {
      window.localStorage.removeItem(key);
    } catch {}
  },

  async multiRemove(keys: readonly string[]): Promise<void> {
    if (!hasStorage()) return;
    try {
      for (const key of keys) window.localStorage.removeItem(key);
    } catch {}
  },

  async clear(): Promise<void> {
    if (!hasStorage()) return;
    try {
      window.localStorage.clear();
    } catch {}
  },
};

export default localStorageAsync;
